import {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useMemo,
  type CSSProperties,
  type KeyboardEvent,
  type Ref,
} from "react";
import { createPortal } from "react-dom";
import { useDossierStore } from "../../store/dossierStore";
import { fetchComptesAuxiliaires } from "../../lib/comptesAuxiliaires";
import {
  isCompteValide,
  isCompteCollectif,
  racineVersCompteCollectif,
} from "../../lib/comptable/formatCompte";
import { cn } from "@/lib/utils";
import { inputFieldClass } from "@/lib/ui/formFieldClasses";

/** Entrée du fichier public/syscohada_comptes_options.json */
export interface SyscohadaCompteOption {
  value: string;
  label: string;
  code: string;
  name: string;
  classe: number;
  level: number;
  searchText: string;
}

export interface CompteAutocompleteProps {
  value: string;
  onChange: (compte: { numero: string; libelle: string }) => void;
  libelle?: string;
  disabled?: boolean;
  placeholder?: string;
  notifyParentWhileTyping?: boolean;
  allowCustom?: boolean;
  inputStyle?: CSSProperties;
  inputRef?: Ref<HTMLInputElement>;
  /** Priorité sur le dossier actif du store (écritures multi-dossiers). */
  dossierId?: string | null;
  /** Si l’utilisateur saisit 8 chiffres valides absents du plan : proposition « Créer » (ex. ouvrir paramètres auxiliaires). */
  onRequestCreateAuxiliaire?: (numero: string) => void;
}

let comptesCache: SyscohadaCompteOption[] | null = null;
let comptesLoadPromise: Promise<SyscohadaCompteOption[]> | null = null;

function loadComptesOnce(): Promise<SyscohadaCompteOption[]> {
  if (comptesCache) return Promise.resolve(comptesCache);
  if (!comptesLoadPromise) {
    comptesLoadPromise = fetch("/syscohada_comptes_options.json")
      .then((r) => {
        if (!r.ok) throw new Error(`SYSCOHADA: ${r.status}`);
        return r.json() as Promise<SyscohadaCompteOption[]>;
      })
      .then((data) => {
        comptesCache = Array.isArray(data) ? data : [];
        return comptesCache;
      })
      .catch((e) => {
        comptesLoadPromise = null;
        throw e;
      });
  }
  return comptesLoadPromise;
}

const MAX_SUGGESTIONS = 24;
const CREATE_SENTINEL = "__create_aux__";

type PickRow = {
  code: string;
  name: string;
  tag: string;
};

function libellePourCompteCollectif(comptes: SyscohadaCompteOption[], code8: string): string {
  const exact = comptes.find((c) => c.code === code8);
  if (exact) return exact.name;
  const candidates = comptes.filter((c) => code8.startsWith(c.code) && /^\d+$/.test(c.code));
  candidates.sort((a, b) => b.code.length - a.code.length);
  return candidates[0]?.name ?? "Compte collectif";
}

function buildPickRows(
  raw: string,
  ohada: SyscohadaCompteOption[],
  aux: { numero: string; libelle: string }[],
  opts: { onCreateNumero: string | null }
): PickRow[] {
  const t = raw.trim();
  if (!t) return [];

  const rows: PickRow[] = [];
  const seen = new Set<string>();

  const push = (code: string, name: string, tag: string) => {
    if (seen.has(code)) return;
    seen.add(code);
    rows.push({ code, name, tag });
  };

  const dOnly = t.replace(/\D/g, "");
  const looksNumeric = t.replace(/\s/g, "").length > 0 && /^\d[\d\s]*$/.test(t.replace(/\s/g, ""));

  if (looksNumeric && dOnly.length >= 1 && dOnly.length <= 8) {
    const col = racineVersCompteCollectif(dOnly);
    if (isCompteValide(col)) {
      const lbl = libellePourCompteCollectif(ohada, col);
      push(col, lbl, isCompteCollectif(col) ? "collectif" : "collectif");
    }
  }

  for (const a of aux) {
    if (a.numero.includes(t) || a.numero.startsWith(dOnly) || a.libelle.toLowerCase().includes(t.toLowerCase())) {
      push(a.numero, a.libelle, "auxiliaire");
    }
  }

  const tLower = t.toLowerCase();
  const ohMatches = ohada.filter(
    (c) => c.code.toLowerCase().startsWith(tLower) || c.searchText.toLowerCase().includes(tLower)
  );
  ohMatches.sort((a, b) => {
    const aStarts = a.code.toLowerCase().startsWith(tLower) ? 0 : 1;
    const bStarts = b.code.toLowerCase().startsWith(tLower) ? 0 : 1;
    if (aStarts !== bStarts) return aStarts - bStarts;
    return a.code.localeCompare(b.code, "fr", { numeric: true });
  });
  for (const c of ohMatches.slice(0, MAX_SUGGESTIONS)) {
    let codeOut = c.code;
    if (/^\d+$/.test(c.code) && c.code.length > 0 && c.code.length < 8) {
      codeOut = racineVersCompteCollectif(c.code);
    }
    const tag = isCompteValide(codeOut) && isCompteCollectif(codeOut) ? "collectif" : "ohada";
    push(codeOut, c.name, tag);
  }

  const limited = rows.slice(0, MAX_SUGGESTIONS);

  if (opts.onCreateNumero && isCompteValide(opts.onCreateNumero)) {
    const exists = seen.has(opts.onCreateNumero);
    if (!exists) {
      limited.push({
        code: CREATE_SENTINEL,
        name: `Créer le compte ${opts.onCreateNumero} ?`,
        tag: "action",
      });
    }
  }

  return limited;
}

const DROPDOWN_MAX_H = 280;
const DROPDOWN_GAP = 4;
/** Au-dessus des modales plein écran (ex. EcritureEditModal z-index 10050). */
const DROPDOWN_Z_INDEX = 11000;

function computeDropdownRect(inputEl: HTMLInputElement, nSuggestions: number): {
  left: number;
  width: number;
  top?: number;
  bottom?: number;
  maxHeight: number;
} {
  const rect = inputEl.getBoundingClientRect();
  const rowApprox = 44;
  const chrome = 8;
  const idealH = Math.min(DROPDOWN_MAX_H, Math.max(nSuggestions, 1) * rowApprox + chrome);
  const spaceBelow = window.innerHeight - rect.bottom - DROPDOWN_GAP;
  const spaceAbove = rect.top - DROPDOWN_GAP;
  const placeAbove = spaceBelow < Math.min(idealH, 120) && spaceAbove > spaceBelow;

  if (placeAbove) {
    const maxHeight = Math.min(DROPDOWN_MAX_H, Math.max(80, spaceAbove));
    return {
      left: rect.left,
      width: rect.width,
      bottom: window.innerHeight - rect.top + DROPDOWN_GAP,
      maxHeight,
    };
  }
  const maxHeight = Math.min(DROPDOWN_MAX_H, Math.max(80, spaceBelow));
  return {
    left: rect.left,
    width: rect.width,
    top: rect.bottom + DROPDOWN_GAP,
    maxHeight,
  };
}

export default function CompteAutocomplete({
  value,
  onChange,
  libelle: libelleProp = "",
  disabled = false,
  placeholder = "N° compte (8 chiffres)",
  notifyParentWhileTyping = true,
  allowCustom = false,
  inputStyle,
  inputRef: inputRefProp,
  dossierId: dossierIdProp,
  onRequestCreateAuxiliaire,
}: CompteAutocompleteProps) {
  const dossierFromStore = useDossierStore((s) => s.activeDossier?.id ?? null);
  const effectiveDossierId = dossierIdProp !== undefined && dossierIdProp !== null ? dossierIdProp : dossierFromStore;

  const [text, setText] = useState(value);
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [auxRows, setAuxRows] = useState<{ numero: string; libelle: string }[]>([]);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const innerInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const ignoreNextCloseRef = useRef(false);
  const [dropdownRect, setDropdownRect] = useState<ReturnType<typeof computeDropdownRect> | null>(null);

  useEffect(() => {
    setText(value);
  }, [value]);

  useEffect(() => {
    let cancelled = false;
    loadComptesOnce()
      .then(() => {
        if (!cancelled) setLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!effectiveDossierId) {
      setAuxRows([]);
      return;
    }
    let cancelled = false;
    fetchComptesAuxiliaires(effectiveDossierId)
      .then((rows) => {
        if (cancelled) return;
        setAuxRows(rows.map((r) => ({ numero: r.numero.trim(), libelle: r.libelle })));
      })
      .catch(() => {
        if (!cancelled) setAuxRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveDossierId]);

  const allComptes = comptesCache;

  const trimmedText = text.trim();
  const createCandidate =
    onRequestCreateAuxiliaire && isCompteValide(trimmedText)
      ? trimmedText
      : null;
  const knownCreate =
    createCandidate &&
    (allComptes?.some((c) => c.code === createCandidate) ||
      auxRows.some((a) => a.numero === createCandidate))
      ? null
      : createCandidate;

  const suggestions = useMemo(() => {
    if (!loaded || !allComptes || !open) return [];
    return buildPickRows(text, allComptes, auxRows, { onCreateNumero: knownCreate });
  }, [loaded, allComptes, text, open, auxRows, knownCreate]);

  useEffect(() => {
    setHighlight(0);
  }, [text, open]);

  const dropdownContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (wrapRef.current?.contains(t)) return;
      if (dropdownContainerRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const updateDropdownPosition = useCallback(() => {
    const inputEl = innerInputRef.current;
    if (!inputEl || !open) {
      setDropdownRect(null);
      return;
    }
    const n = suggestions.length;
    if (n === 0) {
      setDropdownRect(null);
      return;
    }
    setDropdownRect(computeDropdownRect(inputEl, n));
  }, [open, suggestions]);

  useLayoutEffect(() => {
    updateDropdownPosition();
  }, [updateDropdownPosition, text, open, suggestions]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => updateDropdownPosition();
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    return () => {
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [open, updateDropdownPosition]);

  const resolveLibelle = useCallback(
    (code: string): string => {
      const a = auxRows.find((x) => x.numero === code);
      if (a) return a.libelle;
      const o = allComptes?.find((c) => c.code === code);
      if (o) return o.name;
      const col = racineVersCompteCollectif(code.replace(/\D/g, "").slice(0, 8));
      if (isCompteValide(col) && allComptes) {
        return libellePourCompteCollectif(allComptes, col);
      }
      return libelleProp;
    },
    [auxRows, allComptes, libelleProp]
  );

  const pick = useCallback(
    (row: PickRow) => {
      if (row.code === CREATE_SENTINEL) {
        const n = text.trim();
        if (isCompteValide(n) && onRequestCreateAuxiliaire) {
          onRequestCreateAuxiliaire(n);
        }
        setOpen(false);
        setHighlight(0);
        return;
      }
      setText(row.code);
      onChange({ numero: row.code, libelle: row.name });
      setOpen(false);
      setHighlight(0);
    },
    [onChange, onRequestCreateAuxiliaire, text]
  );

  const commitOnBlur = useCallback(() => {
    const trimmed = text.trim();
    if (trimmed === "") {
      onChange({ numero: "", libelle: "" });
      return;
    }
    if (!allComptes) return;

    /** Filtre grand livre / FEC : préfixe libre, pas d’imposition 8 chiffres au blur. */
    const filterMode = !notifyParentWhileTyping && !allowCustom;
    if (filterMode) {
      const exactOhada = allComptes.find((c) => c.code.toLowerCase() === trimmed.toLowerCase());
      if (exactOhada) {
        let code = exactOhada.code;
        if (/^\d+$/.test(code) && code.length < 8) {
          code = racineVersCompteCollectif(code);
        }
        onChange({ numero: code, libelle: exactOhada.name });
        setText(code);
        return;
      }
      const auxHit = auxRows.find((a) => a.numero === trimmed);
      if (auxHit) {
        onChange({ numero: auxHit.numero, libelle: auxHit.libelle });
        setText(auxHit.numero);
        return;
      }
      onChange({ numero: trimmed, libelle: libelleProp });
      setText(trimmed);
      return;
    }

    const exactOhada = allComptes.find((c) => c.code.toLowerCase() === trimmed.toLowerCase());
    if (exactOhada) {
      let code = exactOhada.code;
      if (/^\d+$/.test(code) && code.length < 8) {
        code = racineVersCompteCollectif(code);
      }
      onChange({ numero: code, libelle: exactOhada.name });
      setText(code);
      return;
    }

    const auxHit = auxRows.find((a) => a.numero === trimmed);
    if (auxHit) {
      onChange({ numero: auxHit.numero, libelle: auxHit.libelle });
      setText(auxHit.numero);
      return;
    }

    if (isCompteValide(trimmed)) {
      const col = trimmed;
      if (allComptes.some((c) => c.code === col)) {
        const o = allComptes.find((c) => c.code === col)!;
        onChange({ numero: col, libelle: o.name });
        setText(col);
        return;
      }
      if (allowCustom) {
        onChange({ numero: trimmed, libelle: libelleProp || resolveLibelle(trimmed) });
        setText(trimmed);
        return;
      }
      setText(value);
      return;
    }

    if (allowCustom) {
      setText(value);
      return;
    }

    setText(value);
  }, [
    text,
    allowCustom,
    notifyParentWhileTyping,
    allComptes,
    auxRows,
    onChange,
    libelleProp,
    resolveLibelle,
    value,
  ]);

  const onInputChange = (v: string) => {
    setText(v);
    if (v === "") {
      onChange({ numero: "", libelle: "" });
      setOpen(false);
      return;
    }
    setOpen(true);
    if (notifyParentWhileTyping) {
      onChange({ numero: v, libelle: libelleProp });
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      return;
    }
    if (!open || suggestions.length === 0) {
      if (e.key === "Enter") {
        e.preventDefault();
        commitOnBlur();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      pick(suggestions[highlight]);
    }
  };

  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.children[highlight] as HTMLElement | undefined;
    el?.scrollIntoView({ block: "nearest" });
  }, [highlight, open]);

  const mergedInputStyle: CSSProperties = { ...inputStyle };

  const tagLabelStyle = (tag: string): CSSProperties => {
    if (tag === "collectif") return { color: "#b45309", fontSize: 11, fontWeight: 600 };
    if (tag === "auxiliaire") return { color: "#0369a1", fontSize: 11, fontWeight: 600 };
    if (tag === "action") return { color: "#7c3aed", fontSize: 11, fontWeight: 600 };
    return { color: "#64748b", fontSize: 11, fontWeight: 600 };
  };

  const dropdownContent =
    open &&
    suggestions.length > 0 &&
    !disabled &&
    dropdownRect &&
    createPortal(
      <div
        ref={dropdownContainerRef}
        style={{
          position: "fixed",
          left: dropdownRect.left,
          width: dropdownRect.width,
          ...(dropdownRect.top != null
            ? { top: dropdownRect.top }
            : { bottom: dropdownRect.bottom }),
          zIndex: DROPDOWN_Z_INDEX,
          maxHeight: dropdownRect.maxHeight,
          overflowY: "auto",
          overflowX: "hidden",
          background: "white",
          border: "1px solid #e2e8f0",
          borderRadius: 12,
          boxShadow: "0 10px 25px -5px rgba(0,0,0,0.12), 0 8px 10px -6px rgba(0,0,0,0.08)",
        }}
      >
        <div ref={listRef} role="listbox">
          {suggestions.map((c, i) => (
            <button
              key={`${c.code}-${i}`}
              type="button"
              role="option"
              aria-selected={i === highlight}
              onMouseDown={(e) => {
                e.preventDefault();
                ignoreNextCloseRef.current = true;
              }}
              onClick={() => pick(c)}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                padding: "10px 12px",
                border: "none",
                background: i === highlight ? "#fff7ed" : "transparent",
                borderLeft: i === highlight ? "2px solid #FF9500" : "2px solid transparent",
                cursor: "pointer",
                fontSize: 14,
                fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
                color: "#0f172a",
              }}
              onMouseEnter={() => setHighlight(i)}
            >
              <span style={{ fontWeight: 600 }}>{c.code === CREATE_SENTINEL ? text.trim() : c.code}</span>
              <span style={{ color: "#64748b" }}> — {c.name}</span>
              {c.tag !== "action" ? (
                <span style={{ display: "block", marginTop: 2 }}>
                  <span style={tagLabelStyle(c.tag)}>
                    {c.tag === "collectif" ? "collectif" : c.tag === "auxiliaire" ? "auxiliaire" : "plan OHADA"}
                  </span>
                </span>
              ) : null}
            </button>
          ))}
        </div>
      </div>,
      document.body
    );

  return (
    <div ref={wrapRef} style={{ position: "relative", width: "100%", overflow: "visible" }}>
      <input
        ref={(el) => {
          innerInputRef.current = el;
          const r = inputRefProp;
          if (!r) return;
          if (typeof r === "function") r(el);
          else (r as { current: HTMLInputElement | null }).current = el;
        }}
        type="text"
        disabled={disabled}
        placeholder={placeholder}
        value={text}
        autoComplete="off"
        spellCheck={false}
        className={cn(inputFieldClass, disabled && "cursor-not-allowed bg-slate-50 text-slate-400")}
        style={mergedInputStyle}
        onChange={(e) => onInputChange(e.target.value)}
        onFocus={() => {
          if (disabled) return;
          setOpen(true);
        }}
        onBlur={() => {
          setTimeout(() => {
            if (ignoreNextCloseRef.current) {
              ignoreNextCloseRef.current = false;
              return;
            }
            setOpen(false);
            setDropdownRect(null);
            commitOnBlur();
          }, 120);
        }}
        onKeyDown={onKeyDown}
      />
      {dropdownContent}
    </div>
  );
}
